import * as React from 'react';
import Grid from '@mui/material/Grid2';
import BaseButton from '../../../../components/BaseButton';
import BaseInput from '../../../../components/BaseInput';
import { observer } from 'mobx-react';
import DataStore from '../../../../store';
import { validateForm } from '../../../../helpers/formValidator';
import { notificationStore } from '../../../../store/Notification';
import { MISSING_FIELDS_MESSAGE } from '../../../../helpers/constants';
import socketStore from '../../../../store/Socket';
import ProgressBar from '../../../../components/ProgressBar';
import {
  LAUNCH_BUTTON,
  PREVIEW_BUTTON,
  SOCKET_USER_UPDATE_EVENT,
} from '../../../../constants/base';
import { validationRules } from '../../../../helpers/validationRules';
import { isUUID } from 'validator';
import { Box } from '@mui/material';
import { useSocketListener } from '../../../../hooks/useSocketListener';
import { ProgressPayload } from '../../../../types/global';

const Terminate = observer(() => {
  const [progress, setProgress] = React.useState(0);
  const [progressMax, setProgressMax] = React.useState(0);
  const [progressMessage, setProgressMessage] = React.useState('');

  React.useEffect(() => {
    showOutput();
  }, []);

  const socket = socketStore.socket;
  const channel = React.useMemo(() => SOCKET_USER_UPDATE_EVENT(), []);
  const onProgress = React.useCallback((data: ProgressPayload): void => {
    setProgress(data.progress ?? 0);
    setProgressMax(data.total ?? 0);
    setProgressMessage(data.message ?? '');
  }, []);
  useSocketListener<ProgressPayload>(socket, channel, onProgress, true);

  const fieldLabels: { [key: string]: string } = {
    jobIds: 'Job IDs',
    slideNumber: 'Slide Number',
    wells: 'Wells',
  };

  const validationSchema = {
    slideNumber: validationRules.slideNumber,
    wells: validationRules.wells,
    jobIds: {
      type: 'string',
      custom: (value: string) => {
        if (!value) {
          return null;
        }
        const jobList = value.split(',').map((j) => j.trim());
        const invalidJobs = jobList.filter((id) => !isUUID(id, 4));
        if (invalidJobs.length > 0) {
          return `Invalid Job ID(s): ${invalidJobs.join(', ')}`;
        }
        return null;
      },
      message: 'Job IDs must be valid UUIDv4s separated by commas.',
      required: false,
    },
  };

  const [errors, setErrors] = React.useState({});

  const [data, setData] = React.useState({
    jobIds: '',
    slideNumber: '',
    wells: '',
  });
  const onChangeData = (event: any) => {
    const { name, value }: any = event.target;
    DataStore.setTerminatePreview(false);
    setData((prevData: any) => {
      const newData = {
        ...prevData,
        [name]: value,
      };
      if (
        name === 'slideNumber' &&
        value.trim() === '' &&
        prevData.wells.trim() !== ''
      ) {
        newData.wells = '';
      }
      return newData;
    });
  };

  const previewRun = async (e: any) => {
    e.preventDefault();
    setProgress(0);
    setProgressMax(0);

    const finalValidationSchema = { ...validationSchema };
    const validError = validateForm(data, finalValidationSchema);
    if (!validError) {
      setErrors({});

      const payload = {
        slideNumber: data.slideNumber,
        wells: data.wells,
        jobIds: data.jobIds
          ? data.jobIds
              .split(',')
              .map((id: string) => id.trim())
              .filter((id: string) => id !== '')
          : [],
      };

      const resData: any = await DataStore.terminatePreviewRequest(payload);
      if (resData) {
        DataStore.showOutput('Show terminate', resData, 'html');
      }
    } else {
      setProgress(0);
      setProgressMax(0);

      const isRequiredMissing = (Object.values(validError) as string[]).some(
        (msg) => msg.toLowerCase().includes('is required')
      );

      const humanizedErrors = Object.entries(validError).reduce(
        (acc: any, [key, message]) => {
          const label = fieldLabels[key] || key;
          const safeMessage =
            typeof message === 'string' ? message : String(message);
          acc[label] = safeMessage.replace(new RegExp(key, 'gi'), label);
          return acc;
        },
        {}
      );
      notificationStore.showMessage(
        isRequiredMissing ? MISSING_FIELDS_MESSAGE : 'Some fields are invalid.',
        'error',
        humanizedErrors
      );

      setErrors(humanizedErrors);
    }
  };

  const showOutput = (resData: any = {}) => {
    DataStore.showOutput('Show terminate', resData, 'html');
  };

  const launchRun = async () => {
    setProgress(0);
    setProgressMax(0);

    await DataStore.terminateLaunchRequest();
  };

  const isPreviewDisabled = () => {
    const hasSlideNumber = data.slideNumber.trim().length > 0;
    const hasJobIds = data.jobIds.trim().length > 0;
    return DataStore.isProcessing || (!hasSlideNumber && !hasJobIds);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 12 }}>
          <BaseInput
            feature={'terminate'}
            label={fieldLabels.jobIds}
            value={data.jobIds}
            name={'jobIds'}
            onChange={onChangeData}
            errors={errors}
            description={'Separate multiple job IDs with commas'}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <BaseInput
            feature={'terminate'}
            label={fieldLabels.slideNumber}
            value={data.slideNumber}
            name={'slideNumber'}
            onChange={onChangeData}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <BaseInput
            feature={'terminate'}
            label={fieldLabels.wells}
            value={data.wells}
            name={'wells'}
            onChange={onChangeData}
            errors={errors}
            isDisabled={!data.slideNumber?.trim()}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 12 }}>
          <Box display='flex' alignItems='center' gap={1}>
            <BaseButton
              text={PREVIEW_BUTTON}
              variant='outlined'
              color='primary'
              onClick={previewRun}
              customStyle={{ marginRight: '10px' }}
              isDisabled={isPreviewDisabled()}
              spinner={DataStore.isProcessing}
            />
            <BaseButton
              text={LAUNCH_BUTTON}
              variant='contained'
              color='primary'
              isDisabled={
                DataStore.isProcessing || !DataStore.isTerminatePreview
              }
              spinner={DataStore.isProcessing}
              onClick={launchRun}
            />
          </Box>
        </Grid>
      </Grid>
      <ProgressBar
        actual={progress}
        max={progressMax}
        message={progressMessage}
      />
    </>
  );
});

export default Terminate;
